import { useMemo, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, CheckCircle, CircleAlert, Clock, X } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { TextField } from "../ds";
import RecipientPicker from "./RecipientPicker";
import {
  invalidateSuggestionCaches,
  updateSuggestionStatus,
} from "../api/ess";
import { isAuthorized } from "../api/auth";
import { statusCategory, useWorkflowConfig } from "../hooks/useWorkflow";
import { apiErrorMessage } from "../utils/apiError";
import { suggestionEvaluations } from "../types/ess";
import type {
  AssignedReviewer,
  Suggestion,
  SuggestionAction,
} from "../types/ess";
import type { UserSearchResult } from "../types/auth";
import type { WorkflowAction, WorkflowConfig } from "../api/workflow";

// Actions that have their own dedicated form card (EvaluationFormCard,
// VerificationCard, ...) which collects params the plain confirm-dialog
// flow below can't supply. `record_verification` requires
// `benefits_observations` (VerificationCard's required "Benefits /
// observations" field) — StatusActionPanel's click handler calls
// `updateSuggestionStatus` with no extra `params`, so rendering its own
// button here duplicated VerificationCard's working button with one that
// always 400s. Excluded from `candidateActions` below rather than only
// from the auth-checked `actions` list, so it never even fires its
// `is_authorized` probe or shows a loading/flicker state.
const FORM_DRIVEN_ACTIONS = new Set(["record_verification"]);

// Category vocabulary is a fixed yaml contract, so a small frontend
// mapping from category → button affordance is acceptable: it does not
// grow with new states or actions. "rejected" reads as terminal-close,
// "completed"/"done"/"closed" as success-check, "in_progress" as
// forward-check, "pending"/"submitted" as schedule.
const CATEGORY_ICONS: Record<string, React.ReactElement> = {
  rejected: <RavenIcon icon={X} />,
  completed: <RavenIcon icon={CheckCircle} />,
  done: <RavenIcon icon={CheckCircle} />,
  closed: <RavenIcon icon={CheckCircle} />,
  in_progress: <RavenIcon icon={Check} />,
  pending: <RavenIcon icon={Clock} />,
  submitted: <RavenIcon icon={Clock} />,
};

// Both transitions out of a committee-review stage require that
// committee's evaluation to already be on record with the outcome the
// action implies — the backend 400s otherwise (TRANSITION_GUARDS in
// ess_service.py): forwarding needs the positive outcome ("Recommend" /
// "Approved"), reject-and-close needs the rejecting one. An evaluation
// left at "In Process" satisfies neither, whatever its score. Mirror
// those preconditions here, keyed by the current status, so they're
// surfaced up front (disabled buttons + inline hint) instead of only
// after the user fills the confirm dialog and submits.
const EVAL_PREREQ: Record<
  string,
  {
    committee: "wc" | "ssc";
    committeeLabel: string;
    forward: { outcome: string; label: string };
    reject: { outcome: string; label: string };
  }
> = {
  accepted_by_hr: {
    committee: "wc",
    committeeLabel: "WC",
    forward: { outcome: "recommend", label: "Recommend" },
    reject: { outcome: "not_recommend", label: "Not Recommend" },
  },
  accepted_by_wc: {
    committee: "ssc",
    committeeLabel: "SC",
    forward: { outcome: "approved", label: "Approved" },
    reject: { outcome: "rejected", label: "Rejected" },
  },
};

// A second class of precondition: data the suggestion needs to carry
// before the transition makes sense, independent of any evaluation.
// `accept_hr` forwards to the Work Committee, and the backend 400s while
// that committee is empty — the suggestion would land on a stage with
// nobody on it. Unlike the evaluation prerequisites above, this one can't
// be satisfied from this panel (seats are filled on CommitteeCard), so the
// button is withheld entirely rather than shown disabled, and the caption
// names the fix. Keyed by action name; `committee` matches the token on
// `assigned_reviewers`.
const ROSTER_PREREQ: Record<
  string,
  { committees: AssignedReviewer["committee"][]; message: string }
> = {
  accept_hr: {
    committees: ["wc", "sc"],
    message:
      "Form the Working and Steering Committees to enable Forward to WC.",
  },
};

function actionIcon(
  config: WorkflowConfig | undefined,
  nextState: string | undefined,
): React.ReactElement {
  if (!nextState) return <RavenIcon icon={Check} />;
  const cat = statusCategory(config, nextState);
  return CATEGORY_ICONS[cat] ?? <RavenIcon icon={Check} />;
}

function isNegative(
  config: WorkflowConfig | undefined,
  nextState: string | undefined,
): boolean {
  if (!nextState) return false;
  return statusCategory(config, nextState) === "rejected";
}

function actionLabel(action: WorkflowAction): string {
  return action.button_label ?? action.description ?? action.name;
}

interface Props {
  suggestion: Suggestion;
}

export default function StatusActionPanel({ suggestion }: Props) {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: workflow } = useWorkflowConfig();
  const [confirmAction, setConfirmAction] = useState<WorkflowAction | null>(
    null,
  );
  const [remarks, setRemarks] = useState("");
  const [recipients, setRecipients] = useState<UserSearchResult[]>([]);

  const closeConfirm = () => {
    setConfirmAction(null);
    setRemarks("");
    setRecipients([]);
  };

  // Candidate transitions are exactly the actions the yaml lists for the
  // current state, minus non-transitioning actions (e.g. `edit_suggestion`
  // surfaces here too but with `next_state == current state` — handled
  // separately on the detail view) and system-fired ones, which the yaml
  // marks by giving no `button_label` (`auto_mark_implemented`).
  // `is_authorized` then filters to those the caller can actually invoke.
  const candidateActions = useMemo<WorkflowAction[]>(() => {
    const state = workflow?.states?.[suggestion.status];
    return (state?.actions ?? []).filter(
      (a) =>
        a.button_label &&
        a.next_state &&
        a.next_state !== suggestion.status &&
        !FORM_DRIVEN_ACTIONS.has(a.name),
    );
  }, [workflow, suggestion.status]);

  // Auth race note: between cache fill and click, permissions can
  // shift (impersonate flow, role change, backend state advance).
  // The mutation's onError → snackbar handles it. staleTime + the
  // (id, status) cache key keep the surface area small.

  const authChecks = useQuery({
    queryKey: [
      "ess-auth",
      suggestion.id,
      suggestion.status,
      candidateActions.map((a) => a.name).join(","),
    ],
    queryFn: async () => {
      // TODO(backend): replace N parallel calls with a single
      // bulkIsAuthorized endpoint, or roll authorization into the
      // workflow-facets response so the frontend gets the action
      // set with permissions in one shot.
      const results = await Promise.all(
        candidateActions.map(async (action) => {
          try {
            const res = await isAuthorized({
              action: action.name,
              entity_type: "ess_suggestion",
              entity_id: suggestion.id,
              workflow: "ess_suggestion",
            });
            return { action, authorized: res.is_authorized };
          } catch {
            return { action, authorized: false };
          }
        }),
      );
      return results.filter((r) => r.authorized).map((r) => r.action);
    },
    enabled: candidateActions.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const mutation = useMutation({
    mutationFn: (action: SuggestionAction) =>
      updateSuggestionStatus(
        suggestion.id,
        action,
        remarks || undefined,
        // `params` is omitted entirely when nobody was picked — an empty
        // list would still be a valid body, but sending one on every
        // transition makes the audit payload lie about intent.
        recipients.length > 0
          ? { additional_recipient_user_ids: recipients.map((u) => u.id) }
          : undefined,
      ),
    onSuccess: () => {
      enqueueSnackbar("Status updated", { variant: "success" });
      invalidateSuggestionCaches(qc, suggestion.id);
      closeConfirm();
    },
    onError: (err: unknown) => {
      enqueueSnackbar(apiErrorMessage(err, "Update failed"), {
        variant: "error",
      });
    },
  });

  if (candidateActions.length === 0) return null;
  if (authChecks.isLoading) {
    return (
      <Stack direction="row" justifyContent="center" py={1}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  const actions = (authChecks.data ?? []) as WorkflowAction[];
  if (actions.length === 0) return null;

  // Roster prerequisites (see ROSTER_PREREQ): drop the action from the
  // button row and surface its message in the caption instead.
  const roster = suggestion.assigned_reviewers ?? [];
  const rosterMessages: string[] = [];
  const available = actions.filter((a) => {
    const req = ROSTER_PREREQ[a.name];
    const seated = req?.committees.every((c) =>
      roster.some((r) => r.committee === c),
    );
    if (!req || seated) return true;
    if (!rosterMessages.includes(req.message)) rosterMessages.push(req.message);
    return false;
  });

  const negative = available.filter((a) => isNegative(workflow, a.next_state));
  const positive = available.filter((a) => !isNegative(workflow, a.next_state));

  // Evaluation preconditions (see EVAL_PREREQ). When the current stage
  // gates its transitions on a recorded committee evaluation, disable
  // whichever button(s) the recorded outcome doesn't unlock and explain
  // what to record first — rather than letting the confirm dialog open
  // only to 400 on submit. No evaluation (or one still "In Process")
  // blocks both directions.
  const prereq = EVAL_PREREQ[suggestion.status];
  const recordedOutcome = prereq
    ? suggestionEvaluations(suggestion)[prereq.committee]?.outcome
    : undefined;
  const forwardBlocked =
    !!prereq && recordedOutcome !== prereq.forward.outcome;
  const rejectionBlocked =
    !!prereq && recordedOutcome !== prereq.reject.outcome;
  const prereqMessage = !prereq
    ? ""
    : forwardBlocked && rejectionBlocked
      ? `Record the ${prereq.committeeLabel} evaluation with outcome “${prereq.forward.label}” to forward, or “${prereq.reject.label}” to close as rejected.`
      : forwardBlocked
        ? `Record the ${prereq.committeeLabel} evaluation with outcome “${prereq.forward.label}” before forwarding.`
        : `Record the ${prereq.committeeLabel} evaluation with outcome “${prereq.reject.label}” before closing as rejected.`;
  const showPrereqHint =
    (rejectionBlocked && negative.length > 0) ||
    (forwardBlocked && positive.length > 0);
  const hints = showPrereqHint
    ? [...rosterMessages, prereqMessage]
    : rosterMessages;

  return (
    <>
      <Box
        data-tour="detail-actions"
        sx={{
          position: "fixed",
          bottom: 0,
          left: { xs: 0, md: "240px" },
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          px: { xs: 2, md: 3 },
          py: 1.5,
        }}
      >
        <Stack spacing={1}>
          {hints.map((hint) => (
            <Stack
              key={hint}
              direction="row"
              spacing={0.75}
              alignItems="center"
              justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              sx={{ color: "warning.main" }}
            >
              <RavenIcon
                icon={CircleAlert}
                strokeWidth={1.75}
                sx={{ fontSize: 14 }}
              />
              <Typography
                variant="caption"
                sx={{ color: "warning.main", fontWeight: 500 }}
              >
                {hint}
              </Typography>
            </Stack>
          ))}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={{ xs: "stretch", sm: "flex-end" }}
            spacing={1.5}
          >
            {negative.map((action) => {
              const button = (
                <Button
                  key={action.name}
                  variant="outlined"
                  color="error"
                  startIcon={actionIcon(workflow, action.next_state)}
                  onClick={() => setConfirmAction(action)}
                  disabled={rejectionBlocked}
                  size="medium"
                  fullWidth
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    // Full-width on xs, so a long label wraps rather than
                    // pushing the footer past the viewport.
                    whiteSpace: { xs: "normal", sm: "nowrap" },
                    // Touch-compliant target (≥44px) on tab + mobile.
                    // Desktop keeps the standard MUI medium height.
                    minHeight: { xs: 48, md: 36 },
                  }}
                >
                  {actionLabel(action)}
                </Button>
              );
              // A disabled button can't fire the Tooltip's events, so wrap
              // it in a span (per MUI) — long-press/hover then explains why.
              return rejectionBlocked ? (
                <Tooltip
                  key={action.name}
                  title={prereqMessage}
                  enterTouchDelay={0}
                  arrow
                >
                  <Box
                    component="span"
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      display: "inline-flex",
                    }}
                  >
                    {button}
                  </Box>
                </Tooltip>
              ) : (
                button
              );
            })}
            {positive.map((action) => {
              const cat = statusCategory(workflow, action.next_state);
              const isPending = cat === "pending" || cat === "submitted";
              const button = (
                <Button
                  key={action.name}
                  variant={isPending ? "outlined" : "contained"}
                  color={isPending ? "warning" : "success"}
                  startIcon={actionIcon(workflow, action.next_state)}
                  onClick={() => setConfirmAction(action)}
                  disabled={forwardBlocked}
                  size="medium"
                  fullWidth
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    // Full-width on xs, so a long label wraps rather than
                    // pushing the footer past the viewport.
                    whiteSpace: { xs: "normal", sm: "nowrap" },
                    // Touch-compliant target (≥44px) on tab + mobile.
                    // Desktop keeps the standard MUI medium height.
                    minHeight: { xs: 48, md: 36 },
                  }}
                >
                  {actionLabel(action)}
                </Button>
              );
              // Same disabled-button Tooltip pattern as the reject side.
              return forwardBlocked ? (
                <Tooltip
                  key={action.name}
                  title={prereqMessage}
                  enterTouchDelay={0}
                  arrow
                >
                  <Box
                    component="span"
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      display: "inline-flex",
                    }}
                  >
                    {button}
                  </Box>
                </Tooltip>
              ) : (
                button
              );
            })}
          </Stack>
        </Stack>
      </Box>

      <Dialog
        open={confirmAction !== null}
        onClose={closeConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmAction ? actionLabel(confirmAction) : ""}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {/*
              Confirmation copy splits by action category. Negative
              actions (rejections) are terminal — be explicit about
              irreversibility. Positive actions (forward transitions)
              shouldn't read as scary.
            */}
            {confirmAction && isNegative(workflow, confirmAction.next_state)
              ? "Rejections are terminal. The submitter will see this decision; please add a remark explaining the call."
              : "Confirm this transition. The submitter will see the change; add an optional remark for context."}
          </Typography>
          <TextField
            label="Remarks (optional)"
            placeholder="Add notes about the evaluation..."
            multiline
            minRows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
          />
          <Box sx={{ mt: 2 }}>
            <RecipientPicker value={recipients} onChange={setRecipients} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={closeConfirm}
            sx={{
              whiteSpace: "nowrap",
              minHeight: { xs: 48, md: 36 },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={
              confirmAction && isNegative(workflow, confirmAction.next_state)
                ? "error"
                : "success"
            }
            startIcon={
              !mutation.isPending && confirmAction
                ? actionIcon(workflow, confirmAction.next_state)
                : undefined
            }
            disabled={mutation.isPending}
            onClick={() => {
              if (confirmAction) mutation.mutate(confirmAction.name);
            }}
            sx={{
              whiteSpace: "nowrap",
              minHeight: { xs: 48, md: 36 },
            }}
          >
            {mutation.isPending
              ? "Saving..."
              : confirmAction
                ? actionLabel(confirmAction)
                : ""}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
